import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate, useLocation } from 'react-router-dom'
import {
    CssBaseline,
    AppBar,
    Toolbar,
    Typography,
    Card,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    styled
} from '@material-ui/core'
import syncYourAccountingStyles from './sync-your-accounting.styles'
import CONSTANTS from '../../utils/constants'
import { CustomTextArea, RadioButtonRender, CalendarPickerRender } from './sync-your-accounting-form-fields'
import { useDispatch } from 'react-redux'
import { useCodatState, useDisableButton } from '../../stores/ui/ui.hooks'
import NeoWrapperDefaultTopBar from '../neo-wrapper/neo-wrapper-default-top-bar/neo-wrapper-default-top-bar'
import { CodatButton } from '../custom/custom-codat-button'
import { setDisableButtonCheck, setSnackBar, setCodatState, setCurrentStepperIndex } from '../../stores/ui/ui-action'
import { saveLegalDisclaimerAccepted } from '../../stores/user/user-action'
import AccountLookupModal from '../custom/forms/AccountLookupModal'
import { PopoverTooltip } from '../portico/tooltip/tooltip'
import HelpIcon from '@material-ui/icons/Help'
import { fetchCodatConnectionInformation } from '../../stores/async/async-actions'
import { extractCodatResponse } from '../../utils/codat-utils'
import closeIcon from '../../assets/close.svg'
import shieldIcon from '../../assets/shield-white.svg'
import Logo from '../../assets/logo.svg'
import { useLegalDisclaimerAccepted, useUser } from '../../stores/user/user.hooks'
import { FeatureToggleContext } from '../../config/toggle.context'
import { submitI2CData } from '../../services/bff-service'
import CustomLoading from '../custom/custom-loading'
import { CodatState } from '../../stores/ui/ui-state'
import { shouldShowDisclaimer } from './sync-your-accounting-utils'
import { getFromLocalStorage, itemExists, saveToLocalStorage } from '../../utils/local-storage-manager'
import { setMedalliaCompanyInfo } from '../../utils/medallia-utils'
import { selectCurrentADF } from '../../utils/adf-utils'
import { ADFState } from '../../stores/user/user-state'
import STATIC_TEXT from '../../content/common.json'
import { FCB_PRIVACY_STATEMENT } from '../../utils/urls'

const DisclaimerDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paperWidthSm': {
        width: '536px',
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: '24px',
        borderRadius: '4px',
        background: 'var(--neutrals-grey-00, #FFF)'
    }
}))

const DataPullDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paperWidthSm': {
        maxWidth: '536px'
    },
    '& .MuiDialogContent-root': {
        padding: '8px 8px 0px 8px'
    }
}))

export const SyncYourAccounting = () => {
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const [showDataDialog, setShowDataDialog] = useState<boolean>(false)
    const [displayDisclaimer, setDisplayDisclaimer] = useState<boolean>(false)
    const [declinedDisclaimer, setDeclinedDisclaimer] = useState<boolean>(false)
    const dispatch = useDispatch()
    const classes = syncYourAccountingStyles()
    const {
        register,
        handleSubmit,
        watch,
        unregister,
        setValue,
        getValues,
        formState: { isValid, errors }
    } = useForm({
        defaultValues: {
            fiscal_year_end_dt: '',
            accounting_type: '',
            legal_entity_subsidiaries: '',
            additional_information: ''
        },
        mode: 'onChange'
    })
    const disableButton = useDisableButton()
    const inputFormat = CONSTANTS.DEFAULT_DATE_FORMAT_FUNDRAISING
    const minDate = new Date(new Date(new Date().setFullYear(new Date().getFullYear() - 1)).setHours(0, 0, 0, 0)).setDate(
        new Date().getDate() + 1
    )
    const maxDate = new Date()
    const characterMin = 0
    const characterMax = 400
    const [openSyncAccountmodal, setOpenSyncAccountModal] = React.useState(false)
    function useQuery() {
        const { search } = useLocation()
        return useMemo(() => new URLSearchParams(search), [search])
    }
    const query = useQuery()
    const platform = useMemo(() => extractCodatResponse(query), [query])
    const legalDisclaimerAccepted = useLegalDisclaimerAccepted()
    const navigate = useNavigate()
    const { useToggle } = useContext(FeatureToggleContext)
    const toggleLegalDisclaimer = useToggle('ftLegalDisclaimer')
    const user = useUser()
    const ftLegalDisclaimer = toggleLegalDisclaimer(false, true)
    const [currentADFState, setCurrentADFState] = useState<ADFState>(null)
    const codatState: CodatState = useCodatState()

    const showErrors = useCallback(
        (platform) => {
            switch (platform.statusCode) {
                case '403':
                    dispatch(setSnackBar({ show: true, message: 'Add Data Source cancelled.', messageType: 'error' }))
                    break
                case '500':
                    dispatch(
                        setSnackBar({
                            show: true,
                            message: "We weren't able to connect. Try again or upload your documents manually.",
                            messageType: 'error'
                        })
                    )
                    break
                case '501':
                    dispatch(setSnackBar({ show: true, message: 'Platform not supported.', messageType: 'error' }))
                    break
                default:
                    break
            }
        },
        [dispatch]
    )

    const handleOpenSyncModal = () => {
        setOpenSyncAccountModal(true)
    }

    const onClickDisabledButton = () => {
        if (disableButton) {
            dispatch(setSnackBar({ show: true, message: 'Please Fill The Required Fields', description: '', messageType: 'error' }))
        }
    }

    const onSubmit = async (data) => {
        setSubmitting(true)

        let request = {
            ...data,
            legal_entity_name: currentADFState?.legal_entity_name,
            i2c_id: currentADFState?.adf_id,
            consent_accepted_date_time: getFromLocalStorage(localStorage, CONSTANTS.CONSENT_ACCEPTED_DATE_TIME_LOCALSTORAGE_KEY)
        }

        console.log('Submitting data..', request)

        try {
            const response = await submitI2CData(request)
            setSubmitting(false)
            navigate('/connect/thank-you')
        } catch (e) {
            console.log('Error...', e)
            setSubmitting(false)
            dispatch(setSnackBar({ show: true, message: 'Request failed. Please submit again', messageType: 'error' }))
        }
    }

    useEffect(() => {
        if (user.actions) {
            const currentADF = selectCurrentADF(user.actions)

            if (!currentADF) {
                navigate('/connect/thank-you')
            } else {
                setCurrentADFState(currentADF)
            }
        }
    }, [user])

    useEffect(() => {
        const formValues: any = getValues()
        if (Object.keys(errors.valueOf()).length === 0 && isValid) {
            const isEmpty = Object.values(formValues).every((x) => x === null || x === '')
            dispatch(setDisableButtonCheck(isEmpty))
        } else {
            dispatch(setDisableButtonCheck(true))
        }
    }, [isValid])

    useEffect(() => {
        const hasCodatId: boolean = !!currentADFState?.codat_company_id
        if (currentADFState?.adf_id && (!hasCodatId || !!codatState?.companyId)) {
            setLoading(false)
        }
        if (currentADFState?.legal_entity_name || currentADFState?.dba_name) {
            setMedalliaCompanyInfo({
                journeyId: '',
                companyName: currentADFState?.legal_entity_name || currentADFState?.dba_name
            })
        }
    }, [currentADFState, codatState])

    useEffect(() => {
        if (platform.integration && platform.integration === 'codat') {
            dispatch(setCodatState(platform))
            dispatch(setCurrentStepperIndex(platform.step))
            showErrors(platform)
        } else if (currentADFState?.adf_id) {
            dispatch(fetchCodatConnectionInformation(currentADFState?.adf_id))
        }
    }, [platform, dispatch, showErrors, currentADFState?.adf_id])

    useEffect(() => {
        // Wait until we have the state from the call to get user
        if (currentADFState?.adf_id) {
            setDisplayDisclaimer(
                ftLegalDisclaimer &&
                    shouldShowDisclaimer(
                        platform,
                        currentADFState,
                        legalDisclaimerAccepted,
                        itemExists(localStorage, CONSTANTS.CONSENT_ACCEPTED_DATE_TIME_LOCALSTORAGE_KEY)
                    )
            )
        }
    }, [platform, currentADFState, legalDisclaimerAccepted, ftLegalDisclaimer])

    const disclaimerAccepted = () => {
        saveToLocalStorage(localStorage, CONSTANTS.CONSENT_ACCEPTED_DATE_TIME_LOCALSTORAGE_KEY, new Date().toUTCString())
        dispatch(saveLegalDisclaimerAccepted(true))
    }

    const disclaimerRejected = () => {
        setDeclinedDisclaimer(true)
    }

    return (
        <>
            {loading && <CustomLoading message="Loading" />}
            {submitting && <CustomLoading data-testid="saving-dialog" message="Saving data..." />}
            <DisclaimerDialog open={displayDisclaimer} data-testid="legal-disclaimer-dialog">
                <div className={classes.dialogWrapper}>
                    <DialogTitle className={classes.dialogTitle}>Before we begin, please consent to the following:</DialogTitle>
                    <DialogContent className={classes.dialogContent}>
                        <DialogContentText className={classes.dialogContentText}>
                            By clicking “I Consent” below, you agree on behalf of your business, that First-Citizens Bank & Trust Company
                            (“Bank”) may gain access to your business’ accounting software through any application programming interface
                            (“API”) that you establish with the Bank or the Bank’s agents or service providers. You understand by providing
                            this consent on behalf of your business, that the Bank may obtain financial information about your business at
                            any time, in the Bank’s sole discretion, and that the Bank will have visibility into all the financial
                            information maintained by your business’ accounting software. Further, you hereby authorize Bank to view any
                            such financial information accessed via any API for use (1) in administering, servicing, and monitoring any
                            product your business may obtain from the Bank; (2) in evaluating any business credit application; (3) in
                            identifying available product options for your business; (4) in collecting or enforcing any credit product your
                            business has obtained from the Bank; (5) in providing your business’ financial information to you; (6) in
                            offering your business financial products and services; and/or (7) for other related purposes.
                        </DialogContentText>
                        {declinedDisclaimer && (
                            <DialogContentText className={classes.declinedDisclaimerNotice}>
                                *You cannot proceed without consenting
                            </DialogContentText>
                        )}
                    </DialogContent>
                </div>
                <DialogActions className={classes.dialogActions}>
                    <a
                        className={classes.privacyStatementLinkButton}
                        href={FCB_PRIVACY_STATEMENT}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {STATIC_TEXT.codatLink.privacyStatement}
                    </a>
                    <div className={classes.actionButtons}>
                        <Button className={classes.declineButton} data-testid="cancel-btn" onClick={disclaimerRejected}>
                            Decline
                        </Button>
                        <Button className={classes.acceptButton} data-testid="accept-btn" onClick={disclaimerAccepted}>
                            I consent
                        </Button>
                    </div>
                </DialogActions>
            </DisclaimerDialog>

            <DataPullDialog open={showDataDialog} data-testid="what-data-we-pull-dialog">
                <DialogContent>
                    <div className={classes.dataPullMainContainer}>
                        <a className={classes.closeIcon} data-testid="close-dialog-icon" onClick={() => setShowDataDialog(false)}>
                            <img src={closeIcon} />
                        </a>
                        <div className={classes.dataPullContentContainer}>
                            <p className={classes.dataPullHeading}>What data do we pull?</p>
                            <div className={classes.dataPullContents}>
                                <div>We will be pulling the following data from your accounting software each month:</div>
                                <div>
                                    <span className={classes.dataPullSubHeading}>Financial Statements</span>
                                    <ul className={classes.dataPullList}>
                                        <li>Balance Sheet, Profit & Loss</li>
                                    </ul>
                                </div>
                                <div>
                                    <span className={classes.dataPullSubHeading}>Financial Details</span>
                                    <ul className={classes.dataPullList}>
                                        <li>Chart of Accounts</li>
                                    </ul>
                                </div>
                                <div>
                                    This information is available from your accounting package. These details help us to better understand
                                    your business and provide holistic support.
                                </div>
                            </div>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button className={classes.acceptButton} data-testid="close-dialog-btn" onClick={() => setShowDataDialog(false)}>
                        Close
                    </Button>
                </DialogActions>
            </DataPullDialog>

            <CssBaseline />
            <div className={classes.header}>
                <img className={classes.logo} alt="silicon valley bank" src={Logo} />
                <span className={classes.loggedInEntity}>
                    Welcome, {currentADFState?.legal_entity_name || currentADFState?.dba_name || ''}!
                </span>
            </div>
            <AppBar className={classes.appbar}>
                <Toolbar className={classes.toolbar}>
                    <NeoWrapperDefaultTopBar title="Connect Your Accounting" />
                    <div className={classes.securityBadge}>
                        <img alt="shield" src={shieldIcon} />
                        <span style={{ marginLeft: '8px' }}>Secure Encryption</span>
                    </div>
                </Toolbar>
            </AppBar>
            <div className={classes.flexBoxParent}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Card className={classes.customCard}>
                        <div style={{ margin: '35px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h1>Connect your accounting data</h1>
                            </div>
                            <Typography variant="subtitle2" gutterBottom>
                                Connect your accounting platform for your parent company to avoid needing to manually send us reporting and
                                compliance document (
                                <a
                                    style={{ fontSize: '16px', cursor: 'pointer' }}
                                    data-testid="what-data-we-pull-link"
                                    onClick={() => setShowDataDialog(true)}
                                >
                                    What data do we pull?
                                </a>
                                ).
                            </Typography>

                            <CodatButton
                                handleOpenSyncModal={handleOpenSyncModal}
                                setValue={setValue}
                                register={register}
                                unregister={unregister}
                                name="dataSource"
                                value=""
                                data-testid="data-source"
                                source="adf"
                            />
                            <Typography variant="body1" gutterBottom className={classes.disclosure}>
                                {STATIC_TEXT.codatLink.disclosurePrefix}
                                <a
                                    className={classes.privacyStatementLink}
                                    href={FCB_PRIVACY_STATEMENT}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {STATIC_TEXT.codatLink.privacyStatement}
                                </a>
                                {STATIC_TEXT.codatLink.disclosureSuffix}
                            </Typography>
                        </div>
                    </Card>
                    <Card className={classes.customCard} style={{ marginBottom: '72px' }}>
                        <div style={{ margin: '35px', display: 'flex', flexDirection: 'column', gap: '32.5px' }}>
                            <h1>Additional Details</h1>
                            <div className={classes.cardInnerSection}>
                                <div>
                                    <CalendarPickerRender
                                        fieldName={'fiscal_year_end_dt'}
                                        data-testid="fiscal_year_end_dt"
                                        getValues={getValues}
                                        errors={errors}
                                        register={register}
                                        setValue={setValue}
                                        label={'Last fiscal year-end date'}
                                        unregister={unregister}
                                        minDate={minDate}
                                        maxDate={maxDate}
                                        inputFormat={inputFormat}
                                    />
                                </div>
                                <div>
                                    <Typography variant="subtitle2" className={classes.radioContainer} gutterBottom>
                                        Do you use cash-based or accrual-based accounting?
                                        <PopoverTooltip messageDescription="The main difference between accural and cash basis accounting lies in the timing of when revenue and expenses are recognized. The cash method is a more immediate recognition of revenue and expenses, while the accural method focuses on anticipated revenue and expenses.">
                                            <HelpIcon />
                                        </PopoverTooltip>
                                    </Typography>

                                    <RadioButtonRender
                                        fieldName={'accounting_type'}
                                        data-testid="accounting_type"
                                        value={getValues('accounting_type')}
                                        label={'Do you use cash-based or accrual-based accounting?'}
                                        options={[
                                            { label: 'Cash', value: 'Cash' },
                                            { label: 'Accrual', value: 'Accrual' }
                                        ]}
                                        errors={errors}
                                        register={register}
                                        setValue={setValue}
                                        watch={watch}
                                        minValue={characterMin}
                                        maxValue={characterMax}
                                    />
                                </div>

                                <div>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Please list any and all subsidiaries of your Parent Company (Optional):
                                    </Typography>
                                    <CustomTextArea
                                        value={getValues('legal_entity_subsidiaries')}
                                        data-testid="legal_entity_subsidiaries"
                                        fieldName={'legal_entity_subsidiaries'}
                                        errors={errors}
                                        register={register}
                                        setValue={setValue}
                                        watch={watch}
                                        minValue={characterMin}
                                        maxValue={characterMax}
                                    />
                                </div>

                                <div>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Use the space below to provide any additional information that you think might provide us with a
                                        more complete picture of your company (accounting notes, non-financial updates, projected material
                                        changes, customers, experience, etc.). (Optional)
                                    </Typography>
                                    <CustomTextArea
                                        value={getValues('additional_information')}
                                        data-testid="additional_information"
                                        fieldName={'additional_information'}
                                        errors={errors}
                                        register={register}
                                        setValue={setValue}
                                        watch={watch}
                                        minValue={characterMin}
                                        maxValue={characterMax}
                                    />
                                </div>
                            </div>
                        </div>
                    </Card>

                    <div className={classes.buttoncontainer}>
                        <div className={classes.button}>
                            <Button
                                data-testid="submit-button"
                                className={classes.continueBtn}
                                disabled={disableButton || codatState?.platformName == null || codatState?.platformName == ''}
                                onClick={onClickDisabledButton}
                                type="submit"
                            >
                                {CONSTANTS.SUBMIT}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
            {openSyncAccountmodal && (
                <AccountLookupModal
                    company={currentADFState?.legal_entity_name || currentADFState?.dba_name || ''}
                    adfId={currentADFState?.adf_id}
                    connectRoute="/connect/sync-your-accounting"
                    onCloseModal={() => {
                        setOpenSyncAccountModal(false)
                    }}
                    onChange={() => {}}
                />
            )}
        </>
    )
}
